import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * MFA TOTP enrollment page — full-screen, no dashboard layout.
 *
 * Flow:
 * 1. Calls associateSoftwareToken to get the secret
 * 2. Displays QR code for the authenticator app
 * 3. 6 separate OTP input boxes with auto-focus and auto-submit on 6th digit
 * 4. Calls verifySoftwareToken client-side
 * 5. Confirms on backend (POST /auth/mfa-setup { action: "confirm" })
 * 6. Redirects to dashboard
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { getAuthConfig } from '../config';
import { associateSoftwareToken, verifySoftwareToken } from '../services/cognito-auth';
import { authApi } from '../services/api-client';
export function MfaSetupPage({ redirectTo = '/dashboard' }) {
    const navigate = useNavigate();
    const config = getAuthConfig();
    const user = useAuthStore((s) => s.user);
    const setMfaConfigured = useAuthStore((s) => s.setMfaConfigured);
    const [secretCode, setSecretCode] = useState('');
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState('');
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const inputRefs = useRef([]);
    // Generate TOTP secret on mount
    useEffect(() => {
        associateSoftwareToken()
            .then(({ secretCode: code }) => {
            setSecretCode(code);
            setLoading(false);
        })
            .catch((err) => {
            setError(err.message || 'Errore nella generazione del codice QR');
            setLoading(false);
        });
    }, []);
    // Build otpauth:// URI for QR code
    const otpauthUri = secretCode
        ? `otpauth://totp/${encodeURIComponent(config.login.projectName)}:${encodeURIComponent(user?.email || '')}?secret=${secretCode}&issuer=${encodeURIComponent(config.login.projectName)}`
        : '';
    // QR code as image URL via Google Charts API (no extra dependency)
    const qrCodeUrl = otpauthUri
        ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUri)}`
        : '';
    const handleOtpChange = useCallback((index, value) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const newValues = [...otpValues];
        newValues[index] = digit;
        setOtpValues(newValues);
        setError('');
        // Auto-focus next input
        if (digit && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
        // Auto-submit when all 6 digits are filled
        if (digit && index === 5) {
            const code = newValues.join('');
            if (code.length === 6) {
                handleVerify(code);
            }
        }
    }, [otpValues]);
    const handleKeyDown = useCallback((index, e) => {
        if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    }, [otpValues]);
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            const newValues = pasted.split('');
            setOtpValues(newValues);
            inputRefs.current[5]?.focus();
            handleVerify(pasted);
        }
    }, []);
    const handleVerify = async (code) => {
        setVerifying(true);
        setError('');
        try {
            // Step 1: Verify client-side with Cognito
            await verifySoftwareToken(code);
            // Step 2: Confirm on backend (update DB)
            await authApi.post('/auth/mfa-setup', { action: 'confirm' });
            // Step 3: Update store and redirect
            setMfaConfigured(true);
            navigate(redirectTo, { replace: true });
        }
        catch (err) {
            // Clear OTP and refocus first input
            setOtpValues(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
            if (err.code === 'EnableSoftwareTokenMFAException' || err.message?.includes('Code mismatch')) {
                setError('Codice non valido. Riprova.');
            }
            else {
                setError(err.message || 'Errore durante la verifica. Riprova.');
            }
        }
        finally {
            setVerifying(false);
        }
    };
    if (loading) {
        return (_jsx("div", { className: "ipsoft-mfa-setup-page", children: _jsx("div", { className: "ipsoft-mfa-setup-card", children: _jsx("p", { children: "Caricamento..." }) }) }));
    }
    return (_jsx("div", { className: "ipsoft-mfa-setup-page", children: _jsxs("div", { className: "ipsoft-mfa-setup-card", children: [_jsx("h1", { children: "Configura Autenticazione a Due Fattori" }), _jsx("p", { className: "ipsoft-text-muted", children: "Scansiona il codice QR con la tua app di autenticazione (Google Authenticator, Microsoft Authenticator, ecc.)" }), qrCodeUrl && (_jsx("div", { className: "ipsoft-mfa-qr-container", children: _jsx("img", { src: qrCodeUrl, alt: "QR Code per configurazione TOTP", width: 200, height: 200 }) })), _jsxs("p", { className: "ipsoft-text-muted", style: { fontSize: '0.85rem', marginTop: '0.5rem' }, children: ["Se non riesci a scansionare il QR, inserisci manualmente questo codice: ", _jsx("code", { children: secretCode })] }), _jsxs("div", { style: { marginTop: '1.5rem' }, children: [_jsx("p", { children: _jsx("strong", { children: "Inserisci il codice a 6 cifre dall'app:" }) }), error && (_jsx("div", { className: "ipsoft-alert ipsoft-alert-error", role: "alert", style: { marginBottom: '1rem' }, children: error })), _jsx("div", { className: "ipsoft-otp-inputs", onPaste: handlePaste, children: otpValues.map((val, i) => (_jsx("input", { ref: (el) => { inputRefs.current[i] = el; }, type: "text", inputMode: "numeric", maxLength: 1, value: val, onChange: (e) => handleOtpChange(i, e.target.value), onKeyDown: (e) => handleKeyDown(i, e), className: "ipsoft-otp-input", autoFocus: i === 0, disabled: verifying, "aria-label": `Cifra ${i + 1} di 6` }, i))) }), verifying && (_jsx("p", { className: "ipsoft-text-muted", style: { marginTop: '1rem' }, children: "Verifica in corso..." }))] })] }) }));
}
//# sourceMappingURL=MfaSetupPage.js.map